import { useEffect, useMemo, useState } from 'preact/hooks';
import { fetchJson } from '../../lib/http.js';

const LEVELS = [
  { value: '', label: 'Tümü' },
  { value: 'first_experience', label: 'İlk Tecrübe' },
  { value: 'advisor', label: 'Danışman' },
  { value: 'bronze', label: 'Bronz' },
  { value: 'silver', label: 'Gümüş' },
  { value: 'gold', label: 'Altın' },
  { value: 'platinum', label: 'Platin' },
];

const PAGE_SIZE = 20;

function normalizeRows(data, key) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.ilceler)) return data.ilceler;
  return [];
}

function cityOption(row) {
  const id = row?.Id ?? row?.id;
  const name = row?.Proparcel_text || row?.Proparcel_name || row?.name || id;
  return id == null ? null : { id: String(id), name: String(name || id) };
}

function townOption(row) {
  const id = row?.Id ?? row?.id;
  const name = row?.Proparcel_text || row?.name || id;
  return id == null ? null : { id: String(id), name: String(name || id) };
}

function quarterOption(row) {
  const value = row?.Proparcel_value ?? row?.ProparcelValue;
  const name = row?.Tkgm_text || row?.Proparcel_text || value;
  return value == null ? null : { id: String(value), name: String(name || value) };
}

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

function RatingStars({ value, count }) {
  const numeric = value == null ? null : Math.max(0, Math.min(5, Number(value) || 0));
  const rounded = numeric == null ? 0 : Math.round(numeric);
  return (
    <div className="pp-edc-rating" title={numeric == null ? 'Henüz yıldız puanı yok' : `Yıldız puanı: ${numeric.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`}>
      <span className="pp-edc-rating-label">Kullanıcı Puanı</span>
      <span className="pp-edc-stars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((star) => (
          <i key={star} className={`${star <= rounded ? 'fas' : 'far'} fa-star`} />
        ))}
      </span>
      <span className="pp-edc-rating-text">
        {numeric == null ? 'Puan yok' : numeric.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
        {count ? ` (${count})` : ''}
      </span>
    </div>
  );
}

function ConsultantCard({ row }) {
  const name = row?.full_name || row?.email || 'Danışman';
  const memberLabel = row?.member_label || (row?.is_corporate ? 'Kurumsal Üye' : 'Danışman Üye');
  const icon = row?.is_corporate ? 'fa-building' : 'fa-user-tie';
  const scoreCurrent = row?.expert_score_current ?? 0;
  const scorePeak = row?.expert_score_peak ?? 0;
  const rating = row?.agent_rating_avg_overall;
  const ratingCount = Number(row?.agent_rating_count || 0);
  const phoneHref = row?.phone_display ? `tel:${String(row.phone_display).replace(/[^\d+]/g, '')}` : '';

  return (
    <article className="pp-edc-card">
      <div className="pp-edc-card-header">
        <div className={`pp-edc-avatar ${row?.avatar_url ? '' : 'pp-edc-avatar--placeholder'}`}>
          {row?.avatar_url ? <img src={row.avatar_url} alt={name} loading="lazy" decoding="async" /> : <i className={`fas ${icon}`} />}
        </div>
        <div className="pp-edc-card-title">
          <div>
            <h2>{name}</h2>
            {row?.company_name ? (
              <p className="pp-edc-company" title={row.company_name}>{row.company_name}</p>
            ) : null}
          </div>
          <span className="pp-edc-member"><i className={`fas ${icon}`} /> {memberLabel}</span>
        </div>
      </div>

      <div className="pp-edc-card-stats">
        <span><b>{scoreCurrent}</b><small>Uzmanlık</small></span>
        <span><b>{scorePeak}</b><small>Zirve</small></span>
        <span><b>{ratingCount}</b><small>Yorum</small></span>
      </div>

      <RatingStars value={rating} count={ratingCount} />

      <div className="pp-edc-contact">
        {row?.email ? <a href={`mailto:${row.email}`} title={row.email}><i className="fas fa-envelope" /> {row.email}</a> : <span />}
        {row?.phone_display ? <a href={phoneHref} title={row.phone_display}><i className="fas fa-phone" /> {row.phone_display}</a> : null}
      </div>

      <div className="pp-edc-card-footer">
        <div className="pp-edc-tier">
          {row?.badge_svg_url ? <img src={row.badge_svg_url} alt="" loading="lazy" decoding="async" /> : null}
          <span>{row?.expert_level_label || 'Uzmanlık'}</span>
        </div>
        {row?.profile_url ? <a href={row.profile_url}>Profil <i className="fas fa-arrow-right" /></a> : null}
      </div>
    </article>
  );
}

function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  return (
    <div className="pp-edc-pagination">
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>Önceki</button>
      <span>Sayfa {page} / {totalPages}</span>
      <button type="button" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Sonraki</button>
    </div>
  );
}

export function EmlakDanismanlariApp({ apiUrl, citiesUrl, townsUrl, quartersUrl }) {
  const [cities, setCities] = useState([]);
  const [towns, setTowns] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [filters, setFilters] = useState({ q: '', city_id: '', town_id: '', quarter_value: '', expert_level: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ results: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const debouncedQuery = useDebouncedValue(filters.q);

  useEffect(() => {
    let alive = true;
    fetchJson(citiesUrl)
      .then((payload) => {
        if (!alive) return;
        setCities(normalizeRows(payload).map(cityOption).filter(Boolean));
      })
      .catch(() => {
        if (alive) setCities([]);
      });
    return () => {
      alive = false;
    };
  }, [citiesUrl]);

  useEffect(() => {
    if (!filters.city_id) {
      setTowns([]);
      setQuarters([]);
      return;
    }
    let alive = true;
    fetchJson(`${townsUrl}?CityId=${encodeURIComponent(filters.city_id)}`)
      .then((payload) => {
        if (!alive) return;
        setTowns(normalizeRows(payload, 'ilceler').map(townOption).filter(Boolean));
      })
      .catch(() => {
        if (alive) setTowns([]);
      });
    return () => {
      alive = false;
    };
  }, [filters.city_id, townsUrl]);

  useEffect(() => {
    if (!filters.town_id) {
      setQuarters([]);
      return;
    }
    let alive = true;
    fetchJson(`${quartersUrl}?TownId=${encodeURIComponent(filters.town_id)}`)
      .then((payload) => {
        if (!alive) return;
        setQuarters(normalizeRows(payload, 'quarters').map(quarterOption).filter(Boolean));
      })
      .catch(() => {
        if (alive) setQuarters([]);
      });
    return () => {
      alive = false;
    };
  }, [filters.town_id, quartersUrl]);

  const queryKey = useMemo(() => JSON.stringify({ ...filters, q: debouncedQuery, page }), [filters, debouncedQuery, page]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.city_id) params.set('city_id', filters.city_id);
    if (filters.town_id) params.set('town_id', filters.town_id);
    if (filters.quarter_value) params.set('quarter_value', filters.quarter_value);
    if (filters.expert_level) params.set('expert_level', filters.expert_level);
    if (debouncedQuery.trim()) params.set('q', debouncedQuery.trim());
    params.set('page', String(page));
    params.set('page_size', String(PAGE_SIZE));

    let alive = true;
    setLoading(true);
    setError('');
    fetchJson(`${apiUrl}?${params.toString()}`)
      .then((payload) => {
        if (!alive) return;
        if (!payload?.ok) throw new Error(payload?.error || 'Liste alınamadı.');
        setData(payload);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.message || 'Liste alınamadı.');
        setData({ results: [], total: 0, total_pages: 1 });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [apiUrl, queryKey, debouncedQuery, filters.city_id, filters.town_id, filters.quarter_value, filters.expert_level, page]);

  function patchFilters(next) {
    setFilters((current) => ({ ...current, ...next }));
    setPage(1);
  }

  function resetFilters() {
    setFilters({ q: '', city_id: '', town_id: '', quarter_value: '', expert_level: '' });
    setTowns([]);
    setQuarters([]);
    setPage(1);
  }

  const totalPages = Number(data?.total_pages || 1);

  return (
    <main className="pp-edc-page">
      <section className="pp-edc-hero">
        <div>
          <span className="pp-edc-kicker">ProParcel Danışman Ağı</span>
          <h1>Emlak Danışmanları</h1>
          <p>Uzmanlık bölgesi, firma tipi ve seviyeye göre danışmanları kompakt kartlarla hızlıca tarayın.</p>
        </div>
        <div className="pp-edc-hero-stat">
          <strong>{Number(data?.total || 0).toLocaleString('tr-TR')}</strong>
          <span>kayıt</span>
        </div>
      </section>

      <section className="pp-edc-filters" aria-label="Danışman filtreleri">
        <label className="pp-edc-search">
          <span>Danışman veya firma ara</span>
          <input value={filters.q} type="search" placeholder="İsim, e-posta veya firma adı" onInput={(event) => patchFilters({ q: event.currentTarget.value })} />
        </label>

        <label>
          <span>İl</span>
          <select value={filters.city_id} onChange={(event) => patchFilters({ city_id: event.currentTarget.value, town_id: '', quarter_value: '' })}>
            <option value="">Tümü</option>
            {cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}
          </select>
        </label>

        <label>
          <span>İlçe</span>
          <select value={filters.town_id} disabled={!filters.city_id} onChange={(event) => patchFilters({ town_id: event.currentTarget.value, quarter_value: '' })}>
            <option value="">{filters.city_id ? 'Tüm ilçeler' : 'Önce il seçin'}</option>
            {towns.map((town) => <option key={town.id} value={town.id}>{town.name}</option>)}
          </select>
        </label>

        <label>
          <span>Mahalle</span>
          <select value={filters.quarter_value} disabled={!filters.town_id} onChange={(event) => patchFilters({ quarter_value: event.currentTarget.value })}>
            <option value="">{filters.town_id ? 'Tüm mahalleler' : 'Önce ilçe seçin'}</option>
            {quarters.map((quarter) => <option key={quarter.id} value={quarter.id}>{quarter.name}</option>)}
          </select>
        </label>

        <label>
          <span>Uzmanlık</span>
          <select value={filters.expert_level} onChange={(event) => patchFilters({ expert_level: event.currentTarget.value })}>
            {LEVELS.map((level) => <option key={level.value} value={level.value}>{level.label}</option>)}
          </select>
        </label>

        <button type="button" className="pp-edc-reset" onClick={resetFilters}>Sıfırla</button>
      </section>

      <div className="pp-edc-meta" aria-live="polite">
        {loading ? 'Kayıtlar yükleniyor...' : data?.total ? `${data.total} kayıt bulundu` : 'Sonuç bulunamadı'}
      </div>

      {error ? <div className="pp-edc-state pp-edc-state--error">{error}</div> : null}
      {!error && loading ? <div className="pp-edc-grid">{Array.from({ length: 10 }).map((_, index) => <div key={index} className="pp-edc-card pp-edc-card--skeleton" />)}</div> : null}
      {!error && !loading && !data?.results?.length ? <div className="pp-edc-state">Filtrelere uygun danışman bulunamadı.</div> : null}
      {!error && !loading && data?.results?.length ? (
        <section className="pp-edc-grid" aria-label="Danışman kartları">
          {data.results.map((row) => <ConsultantCard key={row.user_id || row.email || row.profile_url} row={row} />)}
        </section>
      ) : null}

      <Pagination page={page} totalPages={totalPages} onPage={setPage} />
    </main>
  );
}
